import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, of } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

import { ALT_TEXT, COMMON_CONSTANTS, IMAGE_PATHS } from 'src/app/core/constants/common.constant';
import { HEADER_CONST } from 'src/app/core/constants/layout.constants';
import { NOTIFICATION_DEFAULTS } from 'src/app/core/constants/notification.constants';
import { Route } from 'src/app/core/enums/route.enum';

import { AuthLogoutService } from 'src/app/services/auth/auth-logout.service';
import { LogoutFeedbackFlowService } from 'src/app/services/auth/logout-feedback-flow.service';
import { NotificationService } from 'src/app/services/notification/notification.service';
import { UserRole, UserService } from 'src/app/services/user/user.service';
import { NotificationDropdownComponent } from 'src/app/layouts/notification-dropdown/notification-dropdown.component';
import { PartySearchContextService } from 'src/app/services/party-search/party-search-context.service';

import {
  fetchNotificationsForDropdown,
  getCountryIdFromProfile,
  markNotificationsAsRead,
  resolveNotificationCountryId,
} from '@app/utils/notification.utils';
import { ThemeService } from '@app/services/theme/theme.service';

/**
 * Application header: branding, user menu, notifications, and logout.
 * Uses OnPush; user display name/email come from {@link UserService#userProfile}.
 */
@Component({
  selector: 'app-header',
  host: { style: 'contain: layout; display: block;' },
  standalone: true,
  imports: [NgClass, NotificationDropdownComponent],
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  private static readonly HEADER_NOTIFICATIONS_VIEW_SOURCE = 'header-notification-view-all';

  @Input() isDashboardPage = false;

  // ─── Services ──────────────────────────────────────────────────────────────

  readonly notificationStore = inject(NotificationService);
  readonly userService = inject(UserService);

  /** Snapshot of the authenticated user's profile (name, email, country, …). */
  readonly userProfile = this.userService.userProfile;

  // ─── Template constants ────────────────────────────────────────────────────

  readonly IMAGE_PATHS = IMAGE_PATHS;
  readonly HEADER_CONST = HEADER_CONST;
  readonly ALT_TEXT = ALT_TEXT;
  readonly COMMON_CONSTANTS = COMMON_CONSTANTS;

  // ─── UI state ──────────────────────────────────────────────────────────────

  /** Controls the visibility of the user account dropdown. */
  readonly isDropdownOpen = signal(false);

  /** Controls the visibility of the notification panel dropdown. */
  readonly isNotificationDropdownOpen = signal(false);

  /** Unread notification count shown on the bell icon badge — single source of truth from the service. */
  readonly notificationCount = this.notificationStore.notificationCount;

  readonly partySearchContext = inject(PartySearchContextService);
  readonly themeService = inject(ThemeService);

  readonly displayUserName = computed(() => {
    const impersonatedName = this.partySearchContext.impersonatedUserName();
    return impersonatedName ?? this.userProfile()?.name ?? '';
  });

  readonly displayUserEmail = computed(() => {
    const impersonatedData = this.partySearchContext.impersonatedUserApiData();
    if (impersonatedData) {
      return (
        impersonatedData.Email ?? impersonatedData.BusinessEmail ?? impersonatedData.UserEmail ?? ''
      );
    }
    return this.userProfile()?.email ?? '';
  });

  // ─── Private dependencies ──────────────────────────────────────────────────

  private readonly elementRef = inject(ElementRef);
  private readonly authLogout = inject(AuthLogoutService);
  private readonly logoutFeedbackFlow = inject(LogoutFeedbackFlowService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    // Refresh the unread count whenever a party search resolves the impersonated user.
    // Uses the customer's own countryId and userId so notifications are scoped correctly.
    effect(() => {
      this.refreshUnreadCountForImpersonatedUser();
    });
  }

  // ─── Global event listeners ────────────────────────────────────────────────

  /**
   * Closes both dropdowns whenever the user clicks outside the header element.
   * Attached to the `document` so clicks on the backdrop are always caught.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isDropdownOpen.set(false);
      this.isNotificationDropdownOpen.set(false);
    }
  }

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  /**
   * Fetches the unread notification count as soon as the header is initialised
   * so the badge on the bell icon is populated before the user interacts.
   */
  ngOnInit(): void {
    this.loadUnreadCount();
  }

  // ─── Public template actions ───────────────────────────────────────────────

  /**
   * Toggles the user account dropdown and ensures the notification dropdown
   * is closed at the same time (only one panel open at a time).
   */
  public toggleDropdown(): void {
    this.isDropdownOpen.update((open) => !open);
    this.isNotificationDropdownOpen.set(false);
  }

  /**
   * Toggles the notification dropdown.
   * When opening, fetches the first page of notifications for display.
   * Marking as read is deferred until the dropdown closes.
   */
  public toggleNotificationDropdown(): void {
    const shouldOpen = !this.isNotificationDropdownOpen();
    const selectedCountryId = this.resolveEffectiveCountryId();

    this.isNotificationDropdownOpen.set(shouldOpen);
    this.isDropdownOpen.set(false); // ensure user-menu is closed

    if (shouldOpen) {
      fetchNotificationsForDropdown(
        this.notificationStore,
        selectedCountryId,
        this.resolveImpersonatedUserId(),
      )
        .pipe(
          catchError(() => EMPTY),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  /** Closes the notification dropdown panel. */
  public closeNotificationDropdown(): void {
    this.isNotificationDropdownOpen.set(false);
    this.onNotificationDropdownClose();
  }

  /**
   * Clears all cached notification data from the store and resets the badge.
   * Called when the user explicitly dismisses all notifications.
   */
  public clearNotifications(): void {
    this.notificationStore.clearAll('dropdown');
    this.onNotificationsCleared();
  }

  /**
   * Closes the notification dropdown and navigates to the full notifications page.
   */
  public navigateToNotifications(): void {
    this.closeNotificationDropdown();
    this.router.navigate([`/${Route.NOTIFICATIONS}`], {
      queryParams: { source: HeaderComponent.HEADER_NOTIFICATIONS_VIEW_SOURCE },
    });
  }

  /** Placeholder for future dark-mode toggle functionality. */
  public onDarkMode(): void {
    this.themeService.toggle();
    this.isDropdownOpen.set(false);
  }

  /**
   * Initiates the logout flow via {@link AuthLogoutService}.
   * Closes the user dropdown before the logout redirect occurs.
   */
  public onLogout(): void {
    const logoutOptions = {
      beforeLogout: () => {
        this.isDropdownOpen.set(false);
      },
    };

    if (this.logoutFeedbackFlow.requestLogoutFeedback(logoutOptions)) {
      return;
    }

    this.authLogout.logout(logoutOptions);
  }

  /**
   * Closes the user dropdown and navigates to the My Profile page.
   */
  public onMyProfile(): void {
    this.isDropdownOpen.set(false);
    this.router.navigate([`/${Route.MY_PROFILE}`]);
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  /**
   * Called when the notification dropdown closes.
   * Marks all displayed notifications as read and resets the badge count.
   */
  private refreshUnreadCountForImpersonatedUser(): void {
    const role = this.userService.userRoleGate();
    const isInternal = role === UserRole.CNHUser || role === UserRole.Admin;
    if (!isInternal) {
      return;
    }

    const userId = this.partySearchContext.impersonatedUserId();
    if (userId === undefined) {
      return;
    }

    const countryId = this.resolveEffectiveCountryId();
    if (countryId === null) {
      return;
    }

    this.notificationStore
      .getUnreadCount(countryId, userId)
      .pipe(
        catchError(() => of(NOTIFICATION_DEFAULTS.ZERO)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /**
   * Called when the notification dropdown closes.
   * Marks all displayed notifications as read and resets the badge count.
   */
  private onNotificationDropdownClose(): void {
    markNotificationsAsRead(this.notificationStore)
      .pipe(
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        // Reset the badge via the service so badge and dropdown stay in sync.
        this.notificationStore.resetUnreadCount();
      });
  }

  /**
   * Called after all notifications are cleared.
   * Refreshes the unread count badge from the server.
   * Waits for the user profile to be available before resolving the country.
   * The service's unreadCountState is updated by getUnreadCount, so the badge
   * (which reads notificationStore.notificationCount) updates automatically.
   */
  private onNotificationsCleared(): void {
    const userId = this.resolveImpersonatedUserId();
    const countryId = this.resolveEffectiveCountryId();
    if (countryId !== null) {
      this.notificationStore
        .getUnreadCount(countryId, userId)
        .pipe(
          catchError(() => EMPTY),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
      return;
    }
    // Country not yet resolved — wait for profile.
    this.userService
      .getUserProfile$()
      .pipe(
        take(1),
        switchMap((profile) =>
          this.notificationStore
            .getUnreadCount(getCountryIdFromProfile(profile), userId)
            .pipe(catchError(() => EMPTY)),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  /**
   * Waits for the user profile to resolve before fetching the unread count,
   * so the country is always available when the API call is made.
   * The service's unreadCountState is updated inside getUnreadCount via tap,
   * so the badge (reading notificationStore.notificationCount) reacts automatically.
   */
  private loadUnreadCount(): void {
    this.fetchUnreadCount(this.resolveEffectiveCountryId(), this.resolveImpersonatedUserId());
  }

  private fetchUnreadCount(countryId: number | null, userId?: number): void {
    if (countryId !== null || this.isInternalPortalUser()) {
      this.notificationStore
        .getUnreadCount(countryId, userId)
        .pipe(
          catchError(() => of(NOTIFICATION_DEFAULTS.ZERO)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
      return;
    }

    this.userService
      .getUserProfile$()
      .pipe(
        take(1),
        switchMap((profile) =>
          this.notificationStore
            .getUnreadCount(
              resolveNotificationCountryId(
                this.userService.userRoleGate(),
                profile,
                this.partySearchContext.impersonatedCountryId(),
              ),
              userId,
            )
            .pipe(catchError(() => of(NOTIFICATION_DEFAULTS.ZERO))),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private isInternalPortalUser(): boolean {
    const role = this.userService.userRoleGate();
    return (
      role === UserRole.Admin ||
      role === UserRole.CNHUser ||
      role === UserRole.CNHMarketingUser ||
      role === UserRole.CNHNewProfileUser
    );
  }

  /**
   * Returns the impersonated customer's numeric user ID when the logged-in user
   * is a CNH internal user or Admin and a party search is active; otherwise `undefined`.
   */
  private resolveImpersonatedUserId(): number | undefined {
    const role = this.userService.userRoleGate();
    const isInternal = role === UserRole.CNHUser || role === UserRole.Admin;
    return isInternal ? this.partySearchContext.impersonatedUserId() : undefined;
  }

  /** See {@link resolveNotificationCountryId}. */
  private resolveEffectiveCountryId(): number | null {
    return resolveNotificationCountryId(
      this.userService.userRoleGate(),
      this.userProfile(),
      this.partySearchContext.impersonatedCountryId(),
    );
  }
}
